import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Columns3, Eye, EyeOff } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
export interface ColumnVisibility {
  checkbox: boolean;
  abbrev: boolean;
  status: boolean;
  limit: boolean;
  context: boolean;
  sourceText: boolean;
}

interface ColumnVisibilityDropdownProps {
  visibleColumns: ColumnVisibility;
  onVisibilityChange: (columns: ColumnVisibility) => void;
}

const ColumnVisibilityDropdown: React.FC<ColumnVisibilityDropdownProps> = ({
  visibleColumns,
  onVisibilityChange,
}) => {
  const { t } = usePlatformTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Column definitions with labels
  const columns = [
    { key: "checkbox", label: "Select", canToggle: true },
    { key: "abbrev", label: "Loca Key", canToggle: true },
    { key: "status", label: "Source Status", canToggle: true },
    { key: "limit", label: "Limit", canToggle: true },
    { key: "context", label: "Context", canToggle: true },
    { key: "sourceText", label: "Source Text", canToggle: false }, // Always visible
  ];

  // Handle column toggle
  const handleToggle = (columnKey: keyof ColumnVisibility) => {
    // Don't allow toggling sourceText - it's always required
    if (columnKey === "sourceText") return;

    onVisibilityChange({
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey],
    });
  };

  // Count visible columns
  const visibleCount = Object.values(visibleColumns).filter(Boolean).length;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted relative"
          title={t('platform.cards.widgets.column_visibility')}
        >
          <Columns3 className="h-4 w-4" />
          {visibleCount < columns.length && (
            <span className="absolute -top-1 -right-1 bg-purple-P1 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
              {visibleCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="space-y-2">
          <div className="font-medium text-sm mb-3 flex items-center justify-between">
            <span>{t('platform.cards.widgets.column_visibility_2')}</span>
            <span className="text-xs text-muted-foreground">
              {visibleCount} of {columns.length}
            </span>
          </div>
          <div className="space-y-2">
            {columns.map((column) => (
              <label
                key={column.key}
                className={`flex items-center gap-2 p-2 rounded ${
                  column.canToggle
                    ? "cursor-pointer hover:bg-muted"
                    : "cursor-not-allowed opacity-60"
                }`}
              >
                <Checkbox
                  checked={visibleColumns[column.key as keyof ColumnVisibility]}
                  onCheckedChange={() =>
                    handleToggle(column.key as keyof ColumnVisibility)
                  }
                  disabled={!column.canToggle}
                />
                <div className="flex items-center gap-2 flex-1">
                  {visibleColumns[column.key as keyof ColumnVisibility] ? (
                    <Eye className="h-3 w-3 text-muted-foreground" />
                  ) : (
                    <EyeOff className="h-3 w-3 text-muted-foreground" />
                  )}
                  <span className="text-sm">{column.label}</span>
                </div>
                {!column.canToggle && (
                  <span className="text-xs text-muted-foreground">{t('platform.cards.widgets.required')}</span>
                )}
              </label>
            ))}
          </div>
          <div className="pt-2 border-t mt-3">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={() => {
                onVisibilityChange({
                  checkbox: true,
                  abbrev: true,
                  status: true,
                  limit: true,
                  context: true,
                  sourceText: true,
                });
              }}
            >
              Show All
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ColumnVisibilityDropdown;
